import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChefHat, Sparkles, Check, Loader2, Heart, RefreshCw, Mic, MicOff, Send, X, Trash2, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { useListContext } from "../contexts/ListContext";
import { useAuth } from "../contexts/AuthContext";
import PantrySection from "../components/PantrySection";
import RecipeCard from "../components/RecipeCard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Recipes = () => {
  const navigate = useNavigate();
  const { lists, currentListId, setLists } = useListContext();
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [aiRecipes, setAiRecipes] = useState([]);
  const [spoonRecipes, setSpoonRecipes] = useState([]);
  const [expandedRecipe, setExpandedRecipe] = useState(null);
  const [spoonDetails, setSpoonDetails] = useState({});
  const [loadingDetails, setLoadingDetails] = useState(null);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [purchasedItems, setPurchasedItems] = useState([]);
  const [loadingPurchased, setLoadingPurchased] = useState(true);
  const [usePurchased, setUsePurchased] = useState(true);
  const [purchasedDays, setPurchasedDays] = useState(10);
  const [selectedListIds, setSelectedListIds] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [addListId, setAddListId] = useState(null);
  const [favCount, setFavCount] = useState(0);
  const [pantryItems, setPantryItems] = useState([]);
  const [excludedIngredients, setExcludedIngredients] = useState(new Set());
  const [preGenerating, setPreGenerating] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showVoice, setShowVoice] = useState(false);
  const [voiceIngredients, setVoiceIngredients] = useState([]);
  const [isListening, setIsListening] = useState(false);
  const [voiceInput, setVoiceInput] = useState("");
  const [voiceStatus, setVoiceStatus] = useState("");
  const [quickRecipes, setQuickRecipes] = useState([]);
  const [quickLoading, setQuickLoading] = useState(false);
  const recognitionRef = useRef(null);

  // ── Voice handler — EXACT copy of Layout.jsx handleVoiceInput ──
  const handleVoiceInput = useCallback(() => {
    if (!isListening) {
      if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
        setVoiceStatus("Voice recognition is not supported in your browser. Please use Chrome, Edge, or Safari.");
        return;
      }

      const SpeechRecognition = window.webkitSpeechRecognition || window.SpeechRecognition;
      const recognition = new SpeechRecognition();

      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = 'en-US';
      recognition.maxAlternatives = 1;

      recognition.onstart = () => {
        setIsListening(true);
        setVoiceInput("");
        setVoiceStatus("Listening... Speak now!");
      };

      recognition.onresult = (event) => {
        let finalTranscript = '';
        let interimTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }

        if (interimTranscript && !finalTranscript) {
          setVoiceInput(interimTranscript);
          setVoiceStatus("Listening... " + interimTranscript);
        }

        if (finalTranscript) {
          setVoiceInput(finalTranscript);
          // Clean status while the backend parses — no raw transcript echo
          setVoiceStatus("Adding ingredients...");
          processVoiceText(finalTranscript);
        }
      };

      recognition.onerror = (event) => {
        setIsListening(false);
        let msg = "Voice recognition failed. ";
        switch (event.error) {
          case 'no-speech': msg += "No speech was detected. Please try again."; break;
          case 'audio-capture': msg += "Microphone access denied or not available."; break;
          case 'not-allowed': msg += "Microphone permission denied. Please enable microphone access in your browser settings."; break;
          case 'network': msg += "Network error occurred. Please check your connection."; break;
          case 'aborted': msg = "Voice input cancelled."; break;
          default: msg += `Error: ${event.error}. Please try again.`;
        }
        setVoiceStatus(msg);
      };

      recognition.onend = () => {
        setIsListening(false);
      };

      try {
        recognition.start();
        recognitionRef.current = recognition;
      } catch (error) {
        setVoiceStatus("Failed to start voice recognition. Please ensure microphone permissions are enabled.");
        setIsListening(false);
      }
    } else {
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch (e) {}
        recognitionRef.current = null;
      }
      setIsListening(false);
    }
  }, [isListening]);

  const processVoiceText = async (text) => {
    if (!text.trim()) return;
    setVoiceStatus("Adding ingredients...");
    try {
      const res = await fetch(`${BACKEND_URL}/api/recipes/parse-ingredients`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
      });
      if (!res.ok) throw new Error("Parse failed");
      const data = await res.json();
      const ings = data.ingredients || [];
      if (ings.length > 0) {
        setVoiceIngredients(prev => {
          const combined = [...prev];
          ings.forEach(ing => { if (!combined.includes(ing)) combined.push(ing); });
          return combined;
        });
        setVoiceInput("");
        // Friendly confirmation — lists the ingredients that were added
        const formatted =
          ings.length === 1
            ? ings[0]
            : ings.length === 2
            ? `${ings[0]} and ${ings[1]}`
            : `${ings.slice(0, -1).join(", ")}, and ${ings[ings.length - 1]}`;
        setVoiceStatus(`Added ${formatted} to ingredients list.`);
      } else {
        setVoiceInput("");
        setVoiceStatus("Couldn't identify ingredients. Try separating with commas.");
      }
    } catch (err) {
      setVoiceStatus("Failed to parse ingredients. Please try again.");
    }
  };

  const handleTextSubmit = () => {
    const text = voiceInput.trim();
    if (!text) return;
    // Add directly to ingredients list, clear input for next item
    const item = text.toLowerCase();
    setVoiceIngredients(prev => prev.includes(item) ? prev : [...prev, item]);
    setVoiceInput("");
    setVoiceStatus("");
  };

  // Initialize selected lists
  useEffect(() => {
    if (lists.length > 0 && selectedListIds.length === 0) {
      setSelectedListIds(lists.map(l => l.id));
      setAddListId(currentListId);
    }
  }, [lists]); // eslint-disable-line

  // Fetch purchased items + favorites on mount and when days change
  useEffect(() => {
    const fetchData = async () => {
      setLoadingPurchased(true);
      try {
        const [purchRes, favRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/grocery/purchased?days=${purchasedDays}${user?.id ? `&user_id=${user.id}` : ""}`),
          fetch(`${BACKEND_URL}/api/recipes/favorites`)
        ]);
        if (purchRes.ok) {
          const d = await purchRes.json();
          setPurchasedItems(d.items || []);
        }
        if (favRes.ok) {
          const d = await favRes.json();
          setFavorites(d.favorites || []);
          setFavCount((d.favorites || []).length);
        }
      } catch (e) {
        console.error("Failed to fetch data:", e);
      } finally {
        setLoadingPurchased(false);
      }
    };
    fetchData();
  }, [purchasedDays]);

  const getCurrentListIngredients = () => {
    const ingredients = new Set();
    for (const list of lists) {
      if (selectedListIds.includes(list.id)) {
        for (const item of list.items || []) {
          if (item.name) ingredients.add(item.name);
        }
      }
    }
    return Array.from(ingredients);
  };

  const recentPurchases = usePurchased ? purchasedItems.map(i => i.name) : [];
  const currentIngredients = getCurrentListIngredients();
  const stockedPantry = pantryItems.filter(i => i.status === "stocked").map(i => i.name);
  const allIngredientsRaw = Array.from(new Set([...currentIngredients, ...recentPurchases, ...stockedPantry, ...voiceIngredients]));
  const allIngredients = allIngredientsRaw.filter(i => !excludedIngredients.has(i));

  const toggleListSelection = (listId) => {
    setSelectedListIds(prev =>
      prev.includes(listId) ? prev.filter(id => id !== listId) : [...prev, listId]
    );
  };

  // Pending permanent-delete confirmation: { type: 'list'|'purchased', name: string }
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Permanently delete an item from its grocery list
  const permanentlyDeleteFromList = (itemName) => {
    const updatedLists = lists.map(list => {
      if (!selectedListIds.includes(list.id)) return list;
      return { ...list, items: list.items.filter(i => i.name !== itemName) };
    });
    setLists(updatedLists);
    // Also exclude it so it disappears immediately
    setExcludedIngredients(prev => new Set([...prev, itemName]));
  };

  // Permanently delete a recently bought item
  const permanentlyDeletePurchased = async (itemName) => {
    setPurchasedItems(prev => prev.filter(i => i.name !== itemName));
    setExcludedIngredients(prev => new Set([...prev, itemName]));
    // Also delete from backend
    try {
      await fetch(`${BACKEND_URL}/api/grocery/purchased/${encodeURIComponent(itemName)}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: user?.id }),
      });
    } catch { /* silent */ }
  };

  // Confirm & execute the pending delete
  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    const { type, name } = deleteConfirm;
    if (type === "list") {
      permanentlyDeleteFromList(name);
    } else if (type === "purchased") {
      await permanentlyDeletePurchased(name);
    }
    setDeleteConfirm(null);
  };

  // Permanently delete a voice ingredient
  const permanentlyDeleteVoiceIngredient = (idx) => {
    const name = voiceIngredients[idx];
    setVoiceIngredients(prev => prev.filter((_, i) => i !== idx));
    if (name) setExcludedIngredients(prev => new Set([...prev, name]));
  };

  // Pre-generate: check cache on mount when ingredients are available
  useEffect(() => {
    if (allIngredients.length === 0 || hasGenerated) return;
    const checkCache = async () => {
      setPreGenerating(true);
      try {
        const res = await fetch(
          `${BACKEND_URL}/api/recipes/cached?ingredients=${encodeURIComponent(allIngredients.slice(0, 25).join(","))}`
        );
        if (res.ok) {
          const data = await res.json();
          if (data.cached) {
            setAiRecipes(data.ai_recipes || []);
            setSpoonRecipes(data.spoonacular_recipes || []);
            setHasGenerated(true);
          }
        }
      } catch (e) {
        console.error("Cache check failed:", e);
      } finally {
        setPreGenerating(false);
      }
    };
    checkCache();
  }, [allIngredients.length]); // eslint-disable-line

  const generateRecipes = async (forceRefresh = false) => {
    if (allIngredients.length === 0) return;
    if (forceRefresh) setRefreshing(true);
    else setLoading(true);
    setHasGenerated(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/recipes/suggest`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ingredients: allIngredients.slice(0, 25),
          meal_type: "any",
          count: 4,
          force_refresh: forceRefresh,
          dietary_preferences: user?.dietary_preferences || [],
          allergies: user?.allergies || []
        })
      });
      if (res.ok) {
        const data = await res.json();
        setAiRecipes(data.ai_recipes || []);
        setSpoonRecipes(data.spoonacular_recipes || []);
      }
    } catch (e) {
      console.error("Failed to generate recipes:", e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const titleCase = (str) => str.replace(/\b\w/g, c => c.toUpperCase());

  const addMissingToList = (ingredients, targetListId) => {
    const listId = targetListId || addListId || currentListId;
    const targetList = lists.find(l => l.id === listId);
    if (!targetList) return;
    const existing = new Set((targetList.items || []).map(i => i.name.toLowerCase()));
    const missing = ingredients.filter(ing => !ing.have);
    const newItems = missing
      .filter(ing => !existing.has(ing.name.toLowerCase()))
      .map((ing, idx) => ({
        id: Date.now() + Math.random() + idx,
        name: titleCase(ing.name),
        quantity: 1,
        completed: false,
        category: "Other",
        estimated_price: 2.99,
        source: "recipe-suggestion"
      }));
    if (newItems.length > 0) {
      setLists(prev => prev.map(list =>
        list.id === listId ? { ...list, items: [...newItems, ...list.items] } : list
      ));
    }
  };

  // Favorites
  const isFavorited = (recipeName) => {
    return favorites.some(f => f.recipe?.name === recipeName);
  };

  const toggleFavorite = async (recipe, source) => {
    const existing = favorites.find(f => f.recipe?.name === recipe.name);
    if (existing) {
      try {
        await fetch(`${BACKEND_URL}/api/recipes/favorites/${existing.fav_id}`, { method: "DELETE" });
        setFavorites(prev => prev.filter(f => f.fav_id !== existing.fav_id));
        setFavCount(prev => Math.max(0, prev - 1));
      } catch (e) { console.error(e); }
    } else {
      try {
        const res = await fetch(`${BACKEND_URL}/api/recipes/favorites`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ recipe, source })
        });
        if (res.ok) {
          const data = await res.json();
          setFavorites(prev => [{ fav_id: data.fav_id, recipe, source, created_at: new Date().toISOString() }, ...prev]);
          setFavCount(prev => prev + 1);
        }
      } catch (e) { console.error(e); }
    }
  };

  // Lazy-load Spoonacular details on expand
  const handleExpand = async (key, recipe, source) => {
    if (expandedRecipe === key) {
      setExpandedRecipe(null);
      return;
    }
    setExpandedRecipe(key);

    // Only fetch details for Spoonacular recipes that haven't been loaded yet
    if (source === "spoonacular" && recipe.id && !spoonDetails[recipe.id]) {
      setLoadingDetails(key);
      try {
        const ingredientParams = allIngredients.join(",");
        const res = await fetch(
          `${BACKEND_URL}/api/recipes/details/${recipe.id}?ingredients=${encodeURIComponent(ingredientParams)}`
        );
        if (res.ok) {
          const details = await res.json();
          setSpoonDetails(prev => ({ ...prev, [recipe.id]: details }));
        }
      } catch (e) {
        console.error("Failed to fetch recipe details:", e);
      } finally {
        setLoadingDetails(null);
      }
    }
  };

  // Shared recipe card renderer
  const renderRecipeCard = (recipe, key, source) => (
    <RecipeCard
      key={key}
      recipe={recipe}
      recipeKey={key}
      source={source}
      isExpanded={expandedRecipe === key}
      isLoading={loadingDetails === key}
      favorited={isFavorited(recipe.name)}
      spoonDetails={spoonDetails}
      lists={lists}
      addListId={addListId}
      onExpand={handleExpand}
      onToggleFavorite={toggleFavorite}
      onSetAddListId={setAddListId}
      onAddMissing={addMissingToList}
    />
  );

  return (
    <div className="p-4 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Recipes</h2>
          <p className="text-gray-600 text-sm">AI-powered recipes from your grocery list</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowVoice(prev => !prev)}
            className={`transition-colors ${showVoice ? "bg-orange-50 border-orange-300 text-orange-600" : "text-gray-500 border-gray-200 hover:border-orange-300"}`}
            data-testid="toggle-voice-btn"
          >
            <Mic className="h-4 w-4 mr-1" />
            Voice
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/favorites")}
            className="text-red-500 border-red-200 hover:bg-red-50"
            data-testid="go-to-favorites-btn"
          >
            <Heart className={`h-4 w-4 mr-1 ${favCount > 0 ? "fill-current" : ""}`} />
            {favCount > 0 ? favCount : "Saved"}
          </Button>
        </div>
      </div>

      {/* Voice Ingredient Input — same pattern as Home page header */}
      {showVoice && (
        <Card className="border-orange-200 bg-orange-50/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm font-medium text-gray-700">Speak Your Ingredients</p>
                <p className="text-xs text-gray-500">Say what's in your kitchen or type below</p>
              </div>
              <button onClick={() => setShowVoice(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Input + Mic + Send — same layout as Layout.jsx header */}
            <div className="space-y-2">
              <Input
                placeholder='Type an ingredient and press Enter'
                value={voiceInput}
                onChange={(e) => setVoiceInput(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleTextSubmit()}
                className="w-full bg-white border-gray-200 text-gray-900 placeholder-gray-400 text-sm h-10 px-3"
                data-testid="voice-text-input"
              />
              <div className="flex items-center justify-center gap-3">
                <Button
                  onClick={() => { setVoiceInput(""); setVoiceStatus(""); }}
                  variant="ghost"
                  size="sm"
                  className="text-gray-400 hover:text-gray-600 h-8 w-8 p-0"
                  title="Clear"
                >
                  <X className="h-4 w-4" />
                </Button>
                <Button
                  onClick={handleVoiceInput}
                  variant="ghost"
                  size="sm"
                  className={`h-10 w-10 p-0 rounded-full ${
                    isListening
                      ? "bg-red-100 text-red-500 animate-pulse"
                      : "bg-orange-100 text-orange-500 hover:bg-orange-200"
                  }`}
                  title={isListening ? "Stop listening" : "Voice input"}
                  data-testid="voice-mic-btn"
                >
                  {isListening ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
                </Button>
                <Button
                  onClick={handleTextSubmit}
                  variant="ghost"
                  size="sm"
                  className="text-orange-500 hover:text-orange-600 h-8 w-8 p-0"
                  title="Process ingredients"
                  data-testid="voice-send-btn"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Status message — same as Layout.jsx AI response */}
            {voiceStatus && (
              <div className={`mt-3 text-sm rounded-lg px-3 py-2 ${
                voiceStatus.includes("failed") || voiceStatus.includes("denied") || voiceStatus.includes("error")
                  ? "bg-red-50 text-red-600 border border-red-200"
                  : voiceStatus.includes("Listening")
                  ? "bg-blue-50 text-blue-600 border border-blue-200"
                  : "bg-green-50 text-green-600 border border-green-200"
              }`} data-testid="voice-status">
                {voiceStatus}
              </div>
            )}

            {/* Parsed ingredients */}
            {voiceIngredients.length > 0 && (
              <div className="mt-3 space-y-2" data-testid="voice-results">
                <div className="flex flex-wrap gap-1.5">
                  {voiceIngredients.map((ing, idx) => (
                    <Badge key={idx} className="bg-orange-100 text-orange-700 text-sm py-1 px-2.5 flex items-center gap-1" data-testid={`voice-ingredient-${idx}`}>
                      {ing}
                      <button onClick={() => setVoiceIngredients(prev => prev.filter((_, i) => i !== idx))} className="ml-0.5 hover:text-red-600">
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => { setVoiceIngredients([]); setVoiceStatus(""); setQuickRecipes([]); }}
                    variant="outline"
                    size="sm"
                    className="border-red-200 text-red-500 hover:bg-red-50 px-3 shrink-0"
                    data-testid="voice-delete-all-btn"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Clear
                  </Button>
                  <Button
                    onClick={async () => {
                      setQuickLoading(true);
                      try {
                        const res = await fetch(`${BACKEND_URL}/api/recipes/quick-suggest`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({ ingredients: voiceIngredients, count: 4, dietary_preferences: user?.dietary_preferences || [], allergies: user?.allergies || [] })
                        });
                        if (res.ok) {
                          const data = await res.json();
                          setQuickRecipes(data.recipes || []);
                        }
                      } catch (e) {
                        console.error("Quick recipe generation failed:", e);
                      } finally {
                        setQuickLoading(false);
                      }
                    }}
                    disabled={quickLoading}
                    className="flex-1 min-w-0 bg-orange-500 hover:bg-orange-600 text-white"
                    data-testid="voice-use-ingredients"
                  >
                    {quickLoading ? (
                      <><Loader2 className="h-4 w-4 mr-1.5 animate-spin shrink-0" /><span className="truncate">Generating...</span></>
                    ) : (
                      <><Sparkles className="h-4 w-4 mr-1.5 shrink-0" /><span className="truncate">Quick Recipes ({voiceIngredients.length})</span></>
                    )}
                  </Button>
                </div>

                {/* Quick Recipe Results */}
                {quickLoading && (
                  <div className="mt-4 flex flex-col items-center py-6 gap-2">
                    <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center animate-pulse">
                      <ChefHat className="h-6 w-6 text-orange-600" />
                    </div>
                    <p className="text-sm text-gray-600 font-medium">Whipping up quick recipes...</p>
                  </div>
                )}
                {!quickLoading && quickRecipes.length > 0 && (
                  <div className="mt-4 space-y-3" data-testid="quick-recipes-list">
                    <div className="flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-orange-500" />
                      <h3 className="text-sm font-semibold text-gray-700">Quick Recipes</h3>
                      <span className="text-xs text-gray-400">Snacks & Quick Bites</span>
                    </div>
                    {quickRecipes.map((recipe, idx) => renderRecipeCard(recipe, `quick-${idx}`, "ai"))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Voice ingredients pills (when section is collapsed) */}
      {!showVoice && voiceIngredients.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-gray-500 flex items-center gap-1"><Mic className="h-3 w-3" /> Spoken:</span>
          {voiceIngredients.map((ing, idx) => (
            <Badge key={idx} className="bg-orange-100 text-orange-700 text-xs" data-testid={`spoken-ing-${idx}`}>{ing}</Badge>
          ))}
          <button onClick={() => setVoiceIngredients([])} className="text-xs text-gray-400 hover:text-red-500 underline">Clear</button>
        </div>
      )}

      {/* Main Content */}
          {/* Ingredients Summary */}
          <Card className="border-amber-200 bg-amber-50/50">
            <CardContent className="p-4">
              {/* List Selector */}
              <div className="mb-3">
                <p className="text-sm font-medium text-gray-700 mb-2">Generate recipes from</p>
                <div className="flex flex-wrap gap-2">
                  {lists.map((list) => (
                    <button
                      key={list.id}
                      onClick={() => toggleListSelection(list.id)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm transition-colors ${
                        selectedListIds.includes(list.id)
                          ? "bg-amber-600 text-white border-amber-600"
                          : "bg-white text-gray-600 border-gray-300 hover:border-amber-400"
                      }`}
                      data-testid={`list-toggle-${list.id}`}
                    >
                      {selectedListIds.includes(list.id) && <Check className="h-3 w-3" />}
                      {list.name}
                      <span className={`text-xs ${selectedListIds.includes(list.id) ? "text-amber-200" : "text-gray-400"}`}>
                        ({list.items.length})
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* From Selected Lists */}
              <div className="mb-3">
                <p className="text-sm font-medium text-gray-700 mb-1.5">
                  From Selected Lists ({currentIngredients.length})
                </p>
                {currentIngredients.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5 max-h-20 overflow-y-auto">
                    {currentIngredients.map((ing, idx) => {
                      const excluded = excludedIngredients.has(ing);
                      return excluded ? (
                        <button
                          key={idx}
                          onClick={() => setExcludedIngredients(prev => { const n = new Set(prev); n.delete(ing); return n; })}
                          className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-400 rounded-full px-2 py-1 line-through hover:bg-gray-200 transition-colors capitalize"
                          data-testid={`excluded-list-${ing}`}
                        >
                          {ing} +
                        </button>
                      ) : (
                        <span
                          key={idx}
                          className="inline-flex items-center gap-0.5 bg-blue-100 text-blue-700 text-xs rounded-full pl-2.5 pr-0.5 py-1 capitalize"
                          data-testid={`list-chip-${ing}`}
                        >
                          {ing}
                          <button
                            onClick={() => setExcludedIngredients(prev => new Set([...prev, ing]))}
                            className="hover:bg-blue-200 rounded-full p-0.5 transition-colors"
                            title="Exclude from this recipe"
                            data-testid={`remove-list-${ing}`}
                          >
                            <X className="h-3 w-3" />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm({ type: "list", name: ing })}
                            className="hover:bg-red-200 rounded-full p-1 transition-colors text-red-500 hover:text-red-700"
                            title="Permanently remove from list"
                            aria-label={`Permanently remove ${ing} from list`}
                            data-testid={`delete-list-${ing}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-xs text-gray-500">Select a list above to see ingredients.</p>
                )}
              </div>

              {/* Recently Purchased Toggle + Days */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-gray-700">
                      Recently Bought {usePurchased ? `(${purchasedItems.length})` : ""}
                    </p>
                    <button
                      onClick={() => setUsePurchased(prev => !prev)}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                        usePurchased ? "bg-green-500" : "bg-gray-300"
                      }`}
                      data-testid="toggle-purchased"
                    >
                      <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${
                        usePurchased ? "translate-x-[18px]" : "translate-x-[3px]"
                      }`} />
                    </button>
                  </div>
                  {usePurchased && (
                    <select
                      value={purchasedDays}
                      onChange={(e) => setPurchasedDays(Number(e.target.value))}
                      className="border border-gray-300 rounded-lg px-2 py-1 text-xs bg-white text-gray-600"
                      data-testid="purchased-days-select"
                    >
                      {[5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60].map(d => (
                        <option key={d} value={d}>Last {d} days</option>
                      ))}
                    </select>
                  )}
                </div>
                {usePurchased ? (
                  loadingPurchased ? (
                    <p className="text-xs text-gray-400">Loading...</p>
                  ) : purchasedItems.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5 max-h-20 overflow-y-auto">
                      {purchasedItems.slice(0, 15).map((item, idx) => {
                        const excluded = excludedIngredients.has(item.name);
                        return excluded ? (
                          <button
                            key={idx}
                            onClick={() => setExcludedIngredients(prev => { const n = new Set(prev); n.delete(item.name); return n; })}
                            className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-400 rounded-full px-2 py-1 line-through hover:bg-gray-200 transition-colors capitalize"
                            data-testid={`excluded-purchased-${item.name}`}
                          >
                            {item.name} +
                          </button>
                        ) : (
                          <span
                            key={idx}
                            className="inline-flex items-center gap-0.5 bg-green-100 text-green-700 text-xs rounded-full pl-2.5 pr-0.5 py-1 capitalize"
                            data-testid={`purchased-chip-${item.name}`}
                          >
                            {item.name}
                            <button
                              onClick={() => setExcludedIngredients(prev => new Set([...prev, item.name]))}
                              className="hover:bg-green-200 rounded-full p-0.5 transition-colors"
                              title="Exclude from this recipe"
                              data-testid={`remove-purchased-${item.name}`}
                            >
                              <X className="h-3 w-3" />
                            </button>
                            <button
                              onClick={() => setDeleteConfirm({ type: "purchased", name: item.name })}
                              className="hover:bg-red-200 rounded-full p-1 transition-colors text-red-500 hover:text-red-700"
                              title="Permanently delete"
                              aria-label={`Permanently delete ${item.name}`}
                              data-testid={`delete-purchased-${item.name}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </span>
                        );
                      })}
                      {purchasedItems.length > 15 && (
                        <Badge className="text-xs bg-green-50 text-green-600">+{purchasedItems.length - 15} more</Badge>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500">No recent purchases. Clear completed items from your grocery list to track buys.</p>
                  )
                ) : (
                  <p className="text-xs text-gray-400 italic">Recently bought items excluded from recipe generation.</p>
                )}
              </div>

              <div className="mt-3 pt-2 border-t border-amber-200/50">
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-xs text-gray-500">{allIngredients.length} ingredients for recipes</p>
                  {excludedIngredients.size > 0 && (
                    <button
                      onClick={() => setExcludedIngredients(new Set())}
                      className="text-xs text-violet-500 hover:text-violet-700"
                      data-testid="restore-all-ingredients"
                    >
                      Restore all ({excludedIngredients.size})
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto" data-testid="ingredient-chips">
                  {allIngredients.map((item) => (
                    <span
                      key={item}
                      className="inline-flex items-center gap-0.5 bg-amber-100 text-amber-800 text-xs rounded-full pl-2.5 pr-0.5 py-1"
                      data-testid={`ingredient-chip-${item}`}
                    >
                      {item}
                      <button
                        onClick={() => setExcludedIngredients(prev => new Set([...prev, item]))}
                        className="hover:bg-amber-200 rounded-full p-0.5 transition-colors"
                        title="Exclude from this recipe"
                        data-testid={`remove-ingredient-${item}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                  {excludedIngredients.size > 0 && Array.from(excludedIngredients).map((item) => (
                    <button
                      key={`ex-${item}`}
                      onClick={() => setExcludedIngredients(prev => { const n = new Set(prev); n.delete(item); return n; })}
                      className="inline-flex items-center gap-1 bg-gray-100 text-gray-400 text-xs rounded-full px-2.5 py-1 line-through hover:bg-gray-200 transition-colors"
                      data-testid={`excluded-ingredient-${item}`}
                    >
                      {item} +
                    </button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Smart Pantry */}
          <PantrySection onPantryItemsChange={setPantryItems} />

          {/* Generate + Refresh */}
          <div className="space-y-3">
            {!hasGenerated ? (
              <Button
                className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white py-5"
                onClick={() => generateRecipes(false)}
                disabled={loading || preGenerating || allIngredients.length === 0}
                data-testid="generate-recipes-btn"
              >
                {loading || preGenerating ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{preGenerating ? "Checking for cached recipes..." : "Generating recipes..."}</>
                ) : (
                  <><Sparkles className="h-4 w-4 mr-2" />Generate Recipes from My Ingredients</>
                )}
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button
                  className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white py-5"
                  onClick={() => generateRecipes(false)}
                  disabled={loading || refreshing}
                  data-testid="generate-recipes-btn"
                >
                  {loading ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating...</>
                  ) : (
                    <><Sparkles className="h-4 w-4 mr-2" />Generate Recipes</>
                  )}
                </Button>
                <Button
                  variant="outline"
                  className="border-amber-300 text-amber-700 hover:bg-amber-50 py-5 px-4"
                  onClick={() => generateRecipes(true)}
                  disabled={loading || refreshing}
                  data-testid="refresh-recipes-btn"
                >
                  {refreshing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                </Button>
              </div>
            )}
          </div>

          {/* Loading */}
          {loading && (
            <div className="flex flex-col items-center py-12 gap-3">
              <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center animate-pulse">
                <ChefHat className="h-8 w-8 text-amber-600" />
              </div>
              <p className="text-gray-600 font-medium">Creating delicious recipes...</p>
              <p className="text-sm text-gray-400">Analyzing your ingredients</p>
            </div>
          )}

          {/* AI Recipes — organized by meal type */}
          {!loading && !refreshing && aiRecipes.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-amber-500" />
                <h3 className="text-sm font-semibold text-gray-700">Your Meal Plan</h3>
                <span className="text-xs text-gray-400">Breakfast, Lunch, Dinner & Dessert</span>
              </div>
              {aiRecipes.map((recipe, idx) => renderRecipeCard(recipe, `ai-${idx}`, "ai"))}
            </div>
          )}

          {/* Spoonacular Recipes */}
          {!loading && !refreshing && spoonRecipes.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <ChefHat className="h-4 w-4 text-orange-500" />
                <h3 className="text-sm font-semibold text-gray-700">Real Recipe Matches</h3>
              </div>
              {spoonRecipes.map((recipe, idx) => renderRecipeCard(recipe, `spoon-${idx}`, "spoonacular"))}
            </div>
          )}

          {/* Empty State */}
          {!loading && !refreshing && !hasGenerated && (
            <Card className="border-dashed border-2 border-gray-200">
              <CardContent className="p-8 text-center">
                <ChefHat className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 font-medium">Ready to cook?</p>
                <p className="text-sm text-gray-400 mt-1">
                  Tap "Generate Recipes" to get 4 AI-powered meals — breakfast, lunch, dinner & dessert.
                </p>
              </CardContent>
            </Card>
          )}

          {!loading && !refreshing && hasGenerated && aiRecipes.length === 0 && spoonRecipes.length === 0 && (
            <Card>
              <CardContent className="p-8 text-center">
                <ChefHat className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No recipes generated yet</p>
                <p className="text-sm text-gray-400 mt-1">Try adding more ingredients or refreshing</p>
              </CardContent>
            </Card>
          )}

      {/* Dietary Disclaimer */}
      <p className="text-[10px] text-gray-400 text-center px-4 pb-2 leading-snug">
        Recipe suggestions are for informational purposes only. Always verify ingredients for allergens.
        Nutritional data is approximate. <a href="/terms" className="underline">Terms</a>
      </p>

      {/* Permanent-Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => { if (!open) setDeleteConfirm(null); }}>
        <AlertDialogContent data-testid="permanent-delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Permanently remove this item?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently remove{" "}
              <span className="font-semibold text-gray-900">{deleteConfirm?.name}</span>{" "}
              from your {deleteConfirm?.type === "list" ? "grocery list" : "recently bought items"}?
              This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="permanent-delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="permanent-delete-confirm-btn"
            >
              Yes, remove it
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Recipes;
